import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { colors } from '../utils/colors';
import type { Teacher, ActiveTab } from '../utils/types';
import {
  getTeacherMonthlySummary,
  getStudentMonthlySummary,
} from '../Api/attendance';
import type {
  MonthlySummaryMonth,
  MonthlySummaryDay,
} from '../Api/attendance';
import { getTeacherClasses } from '../Api/auth';
import BottomNav from './BottomNav';

interface Props {
  teacher: Teacher;
  onLogout: () => void;
  onNavigate: (tab: ActiveTab) => void;
}

type InsightTab = 'my' | 'student';
type DayStatus = 'present' | 'absent' | 'late';

type MonthStat = {
  month: string;
  present: number | null;
  absent: number | null;
  late: number | null;
};

// ─── Constants ────────────────────────────────────────────────────────────────

const ACADEMIC_MONTHS = [
  'APRIL','MAY','JUNE','JULY','AUGUST','SEPTEMBER',
  'OCTOBER','NOVEMBER','DECEMBER','JANUARY','FEBRUARY','MARCH',
];

function academicYearLabel(): string {
  const now = new Date();
  const y = now.getFullYear();
  const m = now.getMonth();
  const startYear = m >= 3 ? y : y - 1;
  return `${startYear}-${String(startYear + 1).slice(2)}`;
}

// ─── Helpers (My tab calendar) ────────────────────────────────────────────────

const formatTime = (value: string | null | undefined): string => {
  if (!value) return '';
  const tIdx = value.indexOf('T');
  const hhmm =
    tIdx >= 0
      ? value.slice(tIdx + 1, tIdx + 6)
      : /^\d{1,2}:\d{2}/.test(value)
        ? value.slice(0, 5)
        : '';
  if (!/^\d{1,2}:\d{2}$/.test(hhmm)) return '';
  const [hStr, mStr] = hhmm.split(':');
  let h = Number(hStr);
  const m = Number(mStr);
  const ampm = h >= 12 ? 'PM' : 'AM';
  h = h % 12 || 12;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')} ${ampm}`;
};

const parseYearMonth = (monthDate: string): { year: number; month: number } => {
  const [y, m] = monthDate.split('-');
  return { year: Number(y), month: Number(m) - 1 };
};

const buildCalendarCells = (year: number, month: number) => {
  const firstOfMonth = new Date(year, month, 1);
  const offset = (firstOfMonth.getDay() + 6) % 7; // Monday-first
  const daysInMonth = new Date(year, month + 1, 0).getDate();

  const cells: Array<{ day: number | null; date: string | null }> = [];
  for (let i = 0; i < offset; i++) cells.push({ day: null, date: null });
  for (let d = 1; d <= daysInMonth; d++) {
    const date = `${year}-${String(month + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
    cells.push({ day: d, date });
  }
  while (cells.length < 42) cells.push({ day: null, date: null });
  return cells;
};

const STATUS_STYLES: Record<DayStatus, { bg: string; text: string; border: string }> = {
  present: { bg: '#f0fdf4', text: '#166534', border: '#86efac' },
  late:    { bg: '#fef9c3', text: '#854d0e', border: '#facc15' },
  absent:  { bg: '#fff7ed', text: '#92400e', border: '#fcd34d' },
};

const DAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

// ─── Stat Card (Student tab) ──────────────────────────────────────────────────

const StatCard: React.FC<{
  value: number | null;
  label: string;
  color: string;
  bg: string;
  border: string;
  isPercent?: boolean;
}> = ({ value, label, color, bg, border, isPercent }) => (
  <div style={{
    flex: 1,
    background: value === null ? '#f8fafc' : bg,
    border: `1.5px solid ${value === null ? '#e2e8f0' : border}`,
    borderRadius: 14,
    padding: '14px 8px',
    textAlign: 'center',
    minWidth: 0,
  }}>
    <div style={{
      fontSize: 28, fontWeight: 800,
      color: value === null ? '#94a3b8' : color,
      lineHeight: 1, letterSpacing: '-0.5px',
    }}>
      {value === null ? 'NA' : isPercent ? `${value}%` : String(value).padStart(2, '0')}
    </div>
    <div style={{
      fontSize: 10, fontWeight: 700,
      color: value === null ? '#94a3b8' : color,
      letterSpacing: '0.6px', marginTop: 5,
      opacity: value === null ? 0.7 : 1,
    }}>
      {label}
    </div>
  </div>
);

// ─── Month Block (Student tab) ────────────────────────────────────────────────

const MonthBlock: React.FC<{
  label: string;
  present: number | null;
  absent: number | null;
  late: number | null;
  isPercent?: boolean;
}> = ({ label, present, absent, late, isPercent }) => (
  <div style={{
    background: '#fff',
    border: '1.5px solid #f0eaf8',
    borderRadius: 18,
    padding: '18px 16px',
  }}>
    <div style={{
      fontSize: 13, fontWeight: 800, color: colors.text,
      letterSpacing: '0.8px', marginBottom: 14,
    }}>
      {label}
    </div>
    <div style={{ display: 'flex', gap: 10 }}>
      <StatCard value={present} label="PRESENT" color="#166534" bg="#f0fdf4" border="#86efac" isPercent={isPercent} />
      <StatCard value={absent}  label="ABSENT"  color="#92400e" bg="#fff7ed" border="#fcd34d" isPercent={isPercent} />
      <StatCard value={late}    label="LATE"    color="#854d0e" bg="#fef9c3" border="#facc15" isPercent={isPercent} />
    </div>
  </div>
);

// ─── Bell Icon ────────────────────────────────────────────────────────────────

const BellIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none"
    stroke={colors.textMuted} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M18 8A6 6 0 006 8c0 7-3 9-3 9h18s-3-2-3-9"/>
    <path d="M13.73 21a2 2 0 01-3.46 0"/>
  </svg>
);

// ─── Main Component ───────────────────────────────────────────────────────────

const InsightsScreen: React.FC<Props> = ({ teacher, onNavigate }) => {
  const [activeTab, setActiveTab] = useState<InsightTab>('my');

  // ── My tab state (calendar) ──────────────────────────────────────────────
  const [myMonths, setMyMonths] = useState<MonthlySummaryMonth[]>([]);
  const [myDays, setMyDays] = useState<MonthlySummaryDay[]>([]);
  const [activeMonthIdx, setActiveMonthIdx] = useState(0);
  const [activeMonthNumber, setActiveMonthNumber] = useState<number>(
    () => new Date().getMonth() + 1,
  );
  const [myLoading, setMyLoading] = useState(true);
  const [myError, setMyError] = useState('');
  const [previewDay, setPreviewDay] = useState<MonthlySummaryDay | null>(null);
  const isFirstLoadRef = useRef(true);

  // ── Student tab state (original month list) ─────────────────────────────
  const [studentStats, setStudentStats] = useState<MonthStat[]>([]);
  const [studentLoading, setStudentLoading] = useState(false);

  const emptyStats: MonthStat[] = ACADEMIC_MONTHS.map(m => ({
    month: m, present: null, absent: null, late: null,
  }));

  // Track viewport for layout breakpoints
  const [vw, setVw] = useState<number>(
    typeof window !== 'undefined' ? window.innerWidth : 360,
  );
  useEffect(() => {
    const onResize = () => setVw(window.innerWidth);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  const isMobile = vw < 640;
  const isTablet = vw >= 640 && vw < 960;
  const isDesktop = vw >= 960;

  const isSupervisor = teacher.role === 'supervisor';
  const roleLabel = isSupervisor
    ? 'SUPERVISOR'
    : teacher.className
    ? `${teacher.className.toUpperCase()}${teacher.sectionName ? ` – SEC ${teacher.sectionName.toUpperCase()}` : ''}`
    : teacher.role === 'admin'
    ? 'ADMIN'
    : 'SUBJECT TEACHER';

  // ── Fetch My (calendar) summary ──────────────────────────────────────────

  const fetchMySummary = useCallback(async () => {
    setMyLoading(true);
    setMyError('');
    try {
      const data = await getTeacherMonthlySummary(
        Number(teacher.id),
        academicYearLabel(),
        activeMonthNumber,
      );
      setMyMonths(data.months);
      setMyDays(data.days ?? []);

      if (isFirstLoadRef.current) {
        const today = new Date();
        const todayYM = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}`;
        const idxToday = data.months.findIndex(m =>
          m.month_date.startsWith(todayYM),
        );
        if (idxToday >= 0) {
          setActiveMonthIdx(idxToday);
        } else {
          const reversed = [...data.months].reverse();
          const idxFromEnd = reversed.findIndex(m => (m.total_days ?? 0) > 0);
          const idx = idxFromEnd >= 0 ? data.months.length - 1 - idxFromEnd : 0;
          setActiveMonthIdx(idx);
          const monthDate = data.months[idx]?.month_date;
          if (monthDate) setActiveMonthNumber(Number(monthDate.split('-')[1]));
        }
        isFirstLoadRef.current = false;
      }
    } catch (err: any) {
      setMyError(err?.message ?? 'Failed to load summary.');
    } finally {
      setMyLoading(false);
    }
  }, [teacher.id, activeMonthNumber]);

  // ── Fetch Student summary (original) ─────────────────────────────────────

  const fetchStudentStats = useCallback(async () => {
    setStudentLoading(true);
    try {
      const mappings = await getTeacherClasses(Number(teacher.id));
      const mine = mappings.filter(
        m => m.is_active && m.class_teacher_id === Number(teacher.id),
      );

      if (mine.length === 0) {
        setStudentStats(emptyStats);
        return;
      }

      const cls  = mine[0];
      const data = await getStudentMonthlySummary(
        cls.class_id,
        cls.section_id,
        academicYearLabel(),
      );

      setStudentStats(
        data.months.map(m => ({
          month:   m.month,
          present: m.present_pct !== null ? Math.round(m.present_pct) : null,
          absent:  m.absent_pct  !== null ? Math.round(m.absent_pct)  : null,
          late:    m.late_pct    !== null ? Math.round(m.late_pct)    : null,
        })),
      );
    } catch {
      setStudentStats(emptyStats);
    } finally {
      setStudentLoading(false);
    }
  }, [teacher.id]);

  // My tab refetches whenever active month changes
  useEffect(() => {
    fetchMySummary();
  }, [fetchMySummary]);

  // Student tab loads once on mount
  useEffect(() => {
    fetchStudentStats();
  }, [fetchStudentStats]);

  const handleSelectMonth = (idx: number) => {
    setActiveMonthIdx(idx);
    const monthDate = myMonths[idx]?.month_date;
    if (monthDate) {
      const mNum = Number(monthDate.split('-')[1]);
      if (mNum !== activeMonthNumber) setActiveMonthNumber(mNum);
    }
  };

  const activeMonth = myMonths[activeMonthIdx];

  const dailyByDate = useMemo(() => {
    const map = new Map<string, MonthlySummaryDay>();
    myDays.forEach(d => {
      if (d.status) map.set(d.date, d);
    });
    return map;
  }, [myDays]);

  const calendarCells = useMemo(() => {
    if (!activeMonth) return [];
    const { year, month } = parseYearMonth(activeMonth.month_date);
    return buildCalendarCells(year, month);
  }, [activeMonth]);

  const activeYear = activeMonth
    ? parseYearMonth(activeMonth.month_date).year
    : undefined;

  // Responsive sizing (calendar)
  const cellMinHeight = isMobile ? 50 : isTablet ? 68 : 86;
  const cellPaddingY = isMobile ? '6px 8px 18px' : '8px 12px 22px';
  const cellFontSize = isMobile ? 12 : 14;
  const dowFontSize = isMobile ? 10 : 12;
  const dowPaddingY = isMobile ? '8px 4px' : '10px 6px';
  const dowPaddingLeft = isMobile ? 8 : 12;
  const monthTitleFontSize = isMobile ? 14 : 16;
  const monthTitlePadding = isMobile ? '10px 14px' : '14px 20px';

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <div style={{
      height: '100dvh',
      background: '#FDFCFE',
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden',
      fontFamily: "'Inter', sans-serif",
    }}>

      {/* Top bar */}
      <div style={{
        padding: '30px 20px 20px',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        flexShrink: 0,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0 }}>
          <div style={{
            width: 48, height: 48, borderRadius: '50%',
            background: isSupervisor ? '#7C3AED' : '#C4933A',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 16, fontWeight: 800, color: '#fff', flexShrink: 0,
            position: 'relative',
          }}>
            {teacher.name.split(' ').filter(Boolean).slice(0, 2).map(w => w[0].toUpperCase()).join('')}
            {isSupervisor && (
              <div style={{
                position: 'absolute', bottom: -2, right: -2,
                width: 18, height: 18, borderRadius: '50%',
                background: '#FFD700',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 10, border: '2px solid #fff',
              }}>
                ★
              </div>
            )}
          </div>
          <div style={{ minWidth: 0 }}>
            <div style={{
              fontSize: 17, fontWeight: 800, color: colors.text, lineHeight: 1.2,
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            }}>
              {teacher.name}
            </div>
            <div style={{
              fontSize: 11, fontWeight: 700,
              color: isSupervisor ? '#7C3AED' : colors.primary,
              letterSpacing: '0.5px', marginTop: 2,
            }}>
              {roleLabel}
            </div>
          </div>
        </div>
        <button style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4 }}>
          <BellIcon />
        </button>
      </div>

      {/* Year label + tab toggle */}
      <div style={{ padding: '0 20px 16px', flexShrink: 0 }}>
        <div style={{
          fontSize: 11, fontWeight: 700, color: colors.textMuted,
          letterSpacing: '0.8px', marginBottom: 14,
        }}>
          ATTENDANCE SUMMARY - {academicYearLabel()}
        </div>

        <div style={{
          display: 'flex',
          background: '#fff',
          border: `1.5px solid ${colors.borderLight}`,
          borderRadius: 14,
          overflow: 'hidden',
        }}>
          {(['my', 'student'] as InsightTab[]).map(tab => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              style={{
                flex: 1,
                padding: '14px 0',
                background: activeTab === tab ? colors.primaryDark : '#fff',
                border: 'none',
                cursor: 'pointer',
                fontSize: 15,
                fontWeight: 600,
                color: activeTab === tab ? '#fff' : colors.textMuted,
                transition: 'all 0.2s',
                borderRadius: activeTab === tab ? 12 : 0,
              }}
            >
              {tab === 'my' ? 'My' : 'Student'}
            </button>
          ))}
        </div>
      </div>

      {/* ── My tab: calendar UI ──────────────────────────────────────────── */}
      {activeTab === 'my' && (
        <div style={{
          flex: 1,
          overflowY: 'auto',
          WebkitOverflowScrolling: 'touch',
          padding: '0 20px 90px',
        }}>
          {/* Month pill tabs */}
          <div
            className="hide-scrollbar"
            style={{
              display: 'flex',
              gap: 6,
              overflowX: 'auto',
              paddingRight: 12,
              alignItems: 'center',
              marginBottom: 24,
              scrollbarWidth: 'none',
              msOverflowStyle: 'none' as any,
            }}
          >
            {myMonths.map((m, idx) => {
              const isActive = idx === activeMonthIdx;
              return (
                <button
                  key={m.month}
                  onClick={() => handleSelectMonth(idx)}
                  style={{
                    padding: '6px 14px',
                    borderRadius: 999,
                    border: `1.5px solid ${isActive ? colors.primary : colors.border}`,
                    background: isActive ? colors.primary : '#fff',
                    color: isActive ? '#fff' : colors.text,
                    fontSize: 12,
                    fontWeight: isActive ? 700 : 500,
                    cursor: 'pointer',
                    fontFamily: 'inherit',
                    whiteSpace: 'nowrap',
                    flexShrink: 0,
                    transition: 'all 0.15s',
                  }}
                >
                  {m.month}
                </button>
              );
            })}
          </div>

          {myLoading && myMonths.length === 0 ? (
            <div style={{
              padding: '60px 0', textAlign: 'center',
              color: colors.textMuted, fontSize: 14,
            }}>
              Loading summary…
            </div>
          ) : myError ? (
            <div style={{
              padding: '60px 0', textAlign: 'center',
              color: colors.danger, fontSize: 14,
            }}>
              {myError}
            </div>
          ) : activeMonth ? (
            <div style={{
              display: 'grid',
              gridTemplateColumns: isDesktop ? 'minmax(0, 1fr) 300px' : '1fr',
              gap: isDesktop ? 20 : 14,
            }}>
              {/* Calendar card */}
              <div style={{
                background: '#fff',
                border: `1px solid ${colors.border}`,
                borderRadius: 14,
                overflow: 'hidden',
              }}>
                <div style={{
                  background: colors.primary,
                  color: '#fff',
                  padding: monthTitlePadding,
                  fontSize: monthTitleFontSize,
                  fontWeight: 700,
                  textAlign: 'center',
                  letterSpacing: '0.04em',
                }}>
                  {activeMonth.month}
                  {activeYear ? `-${activeYear}` : ''}
                </div>

                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(7, 1fr)',
                  borderBottom: `1px solid ${colors.border}`,
                  background: '#fff',
                }}>
                  {DAY_LABELS.map((d, i) => (
                    <div
                      key={d}
                      style={{
                        padding: dowPaddingY,
                        textAlign: 'left',
                        paddingLeft: dowPaddingLeft,
                        fontSize: dowFontSize,
                        fontWeight: 700,
                        color: i === 6 ? colors.danger : colors.text,
                        borderRight: i < 6 ? `1px solid ${colors.border}` : 'none',
                      }}
                    >
                      {isMobile ? d.charAt(0) : d}
                    </div>
                  ))}
                </div>

                {myLoading ? (
                  <div style={{
                    height: 6 * cellMinHeight,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: colors.textMuted,
                    fontSize: 14,
                  }}>
                    Loading…
                  </div>
                ) : (
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(7, 1fr)',
                  }}>
                    {calendarCells.map((cell, idx) => {
                      const col = idx % 7;
                      const row = Math.floor(idx / 7);
                      const entry = cell.date ? dailyByDate.get(cell.date) : undefined;
                      const status = entry?.status ?? null;
                      const timeLabel = entry?.marked_at_device
                        ? formatTime(entry.marked_at_device)
                        : '';
                      const isSunday = col === 6;
                      const styleForStatus = status ? STATUS_STYLES[status as DayStatus] : null;
                      const isEmpty = !cell.day;
                      const isInactiveSunday = isSunday && !isEmpty && !status;
                      // Only mobile uses the click-to-open drawer; on tablet/desktop
                      // the time is shown inside the cell, so cells are non-interactive.
                      const isClickable = isMobile && !!entry;

                      return (
                        <div
                          key={idx}
                          onClick={isClickable ? () => setPreviewDay(entry!) : undefined}
                          style={{
                            minHeight: cellMinHeight,
                            padding: cellPaddingY,
                            borderRight: col < 6 ? `1px solid ${colors.border}` : 'none',
                            borderTop:   row > 0 ? `1px solid ${colors.border}` : 'none',
                            background: isEmpty
                              ? '#f5f6f8'
                              : styleForStatus
                                ? styleForStatus.bg
                                : isInactiveSunday
                                  ? '#f8fafc'
                                  : '#fff',
                            color: isEmpty
                              ? '#cbd5e1'
                              : styleForStatus
                                ? styleForStatus.text
                                : isInactiveSunday
                                  ? '#94a3b8'
                                  : colors.text,
                            fontSize: cellFontSize,
                            fontWeight: 700,
                            position: 'relative',
                            cursor: isClickable ? 'pointer' : 'default',
                            minWidth: 0,
                          }}
                        >
                          {cell.day ?? ''}
                          {status && timeLabel && !isMobile && (
                            <div style={{
                              position: 'absolute',
                              left: 8,
                              right: 8,
                              bottom: 4,
                              fontSize: 9,
                              fontWeight: 700,
                              fontVariantNumeric: 'tabular-nums',
                              opacity: 0.85,
                              textAlign: 'right',
                            }}>
                              {timeLabel}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Side panel (stats) */}
              <div style={{
                display: isMobile || isTablet ? 'grid' : 'flex',
                gridTemplateColumns: isMobile || isTablet ? 'repeat(2, 1fr)' : undefined,
                flexDirection: isDesktop ? 'column' : undefined,
                gap: 10,
              }}>
                <div style={{
                  background: '#fff',
                  border: `1px solid ${colors.border}`,
                  borderRadius: 12,
                  padding: '12px 14px',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'flex-start',
                  justifyContent: isDesktop ? 'space-between' : 'flex-start',
                  gap: 6,
                }}>
                  <div style={{
                    fontSize: 11, fontWeight: 700, color: colors.textMuted,
                    textTransform: 'uppercase', letterSpacing: '0.06em',
                  }}>
                    Total Working Days
                  </div>
                  <div style={{
                    fontSize: 20, fontWeight: 800, color: colors.text, lineHeight: 1.1,
                  }}>
                    {activeMonth.total_days ?? 0}
                  </div>
                </div>

                {[
                  { key: 'present' as DayStatus, label: 'Present', count: activeMonth.present },
                  { key: 'absent'  as DayStatus, label: 'Absent',  count: activeMonth.absent  },
                  { key: 'late'    as DayStatus, label: 'Late',    count: activeMonth.late    },
                ].map(s => {
                  const sty = STATUS_STYLES[s.key];
                  return (
                    <div
                      key={s.key}
                      style={{
                        background: sty.bg,
                        border: `1px solid ${sty.border}`,
                        borderRadius: 12,
                        padding: '12px 14px',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'flex-start',
                        justifyContent: 'space-between',
                        gap: 6,
                      }}
                    >
                      <div style={{
                        fontSize: 11, fontWeight: 700, color: sty.text,
                        textTransform: 'uppercase', letterSpacing: '0.06em',
                      }}>
                        {s.label}
                      </div>
                      <div style={{
                        fontSize: 20, fontWeight: 800, color: sty.text, lineHeight: 1.1,
                      }}>
                        {String(s.count ?? 0).padStart(2, '0')}{' '}
                        <span style={{
                          fontSize: 11, fontWeight: 600, opacity: 0.75,
                        }}>
                          days
                        </span>
                      </div>
                    </div>
                  );
                })}

                <div style={{
                  background: '#fff',
                  border: `1px solid ${colors.border}`,
                  borderRadius: 12,
                  padding: '10px 14px',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 8,
                  gridColumn: isMobile || isTablet ? '1 / -1' : undefined,
                }}>
                  <div style={{
                    fontSize: 10, fontWeight: 700, color: colors.textMuted,
                    letterSpacing: '0.06em',
                  }}>
                    LEGEND
                  </div>
                  <div style={{
                    display: 'flex', alignItems: 'center', gap: 14,
                    flexWrap: 'wrap',
                  }}>
                    {(['present', 'absent', 'late'] as DayStatus[]).map(s => (
                      <div key={s} style={{
                        display: 'inline-flex', alignItems: 'center', gap: 6,
                      }}>
                        <div style={{
                          width: 12, height: 12, borderRadius: 3,
                          background: STATUS_STYLES[s].bg,
                          border: `1px solid ${STATUS_STYLES[s].border}`,
                        }} />
                        <span style={{
                          fontSize: 12, color: colors.text, textTransform: 'capitalize',
                        }}>
                          {s}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div style={{
              padding: '60px 0', textAlign: 'center',
              color: colors.textMuted, fontSize: 14,
            }}>
              No data available.
            </div>
          )}
        </div>
      )}

      {/* ── Student tab: original month-blocks list ─────────────────────── */}
      {activeTab === 'student' && (
        studentLoading ? (
          <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <div style={{
              width: 30, height: 30,
              border: `3px solid ${colors.borderLight}`,
              borderTopColor: colors.primary,
              borderRadius: '50%',
              animation: 'spin 0.7s linear infinite',
            }} />
          </div>
        ) : (
          <div style={{
            flex: 1,
            overflowY: 'auto',
            WebkitOverflowScrolling: 'touch',
            padding: '0 20px 80px',
            display: 'flex',
            flexDirection: 'column',
            gap: 12,
          }}>
            {studentStats.map(stat => (
              <MonthBlock
                key={stat.month}
                label={stat.month}
                present={stat.present}
                absent={stat.absent}
                late={stat.late}
                isPercent={stat.present !== null}
              />
            ))}
          </div>
        )
      )}

      {/* Day preview — mobile bottom drawer */}
      {previewDay && (
        <div
          onClick={() => setPreviewDay(null)}
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(15, 23, 42, 0.55)',
            display: 'flex',
            alignItems: 'flex-end',
            justifyContent: 'center',
            zIndex: 1000,
          }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{
              background: '#fff',
              borderTopLeftRadius: 20,
              borderTopRightRadius: 20,
              width: '100%',
              maxHeight: '70vh',
              overflow: 'auto',
              boxShadow: '0 -10px 30px rgba(0, 0, 0, 0.18)',
              display: 'flex',
              flexDirection: 'column',
              paddingBottom: 'env(safe-area-inset-bottom, 16px)',
            }}
          >
            {/* Drag handle */}
            <div style={{
              display: 'flex', justifyContent: 'center', paddingTop: 10,
            }}>
              <div style={{
                width: 40, height: 4, borderRadius: 999,
                background: '#cbd5e1',
              }} />
            </div>

            {/* Header */}
            <div style={{
              display: 'flex',
              alignItems: 'flex-start',
              justifyContent: 'space-between',
              padding: '14px 20px 4px',
              gap: 12,
            }}>
              <div style={{ minWidth: 0 }}>
                <div style={{
                  fontSize: 15, fontWeight: 800, color: colors.text,
                }}>
                  {previewDay.date}
                </div>
                {previewDay.status && (
                  <div style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 6,
                    marginTop: 6,
                    padding: '3px 10px',
                    borderRadius: 999,
                    background: STATUS_STYLES[previewDay.status as DayStatus].bg,
                    color:      STATUS_STYLES[previewDay.status as DayStatus].text,
                    border:     `1px solid ${STATUS_STYLES[previewDay.status as DayStatus].border}`,
                    fontSize: 10,
                    fontWeight: 700,
                    textTransform: 'uppercase',
                    letterSpacing: '0.06em',
                  }}>
                    {previewDay.status}
                  </div>
                )}
              </div>
              <button
                onClick={() => setPreviewDay(null)}
                style={{
                  width: 32, height: 32, borderRadius: 8,
                  border: `1px solid ${colors.border}`,
                  background: '#fff', cursor: 'pointer',
                  fontSize: 18, fontWeight: 700,
                  color: colors.textMuted, fontFamily: 'inherit',
                  flexShrink: 0,
                }}
              >
                ×
              </button>
            </div>

            {/* Marked time block */}
            <div style={{
              margin: '14px 20px 8px',
              background: '#f8fafc',
              border: `1px solid ${colors.border}`,
              borderRadius: 12,
              padding: '14px 16px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 12,
            }}>
              <div style={{
                fontSize: 11, fontWeight: 700, color: colors.textMuted,
                textTransform: 'uppercase', letterSpacing: '0.06em',
              }}>
                Marked At
              </div>
              <div style={{
                fontSize: 18, fontWeight: 800, color: colors.text,
                fontVariantNumeric: 'tabular-nums',
              }}>
                {previewDay.marked_at_device
                  ? formatTime(previewDay.marked_at_device)
                  : '—'}
              </div>
            </div>

            {/* Note */}
            {previewDay.note && (
              <div style={{
                padding: '6px 20px 16px',
                fontSize: 13,
                color: colors.text,
              }}>
                <div style={{
                  color: colors.textMuted, fontWeight: 600, marginBottom: 4,
                  fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.06em',
                }}>
                  Note
                </div>
                <div>{previewDay.note}</div>
              </div>
            )}
          </div>
        </div>
      )}

      <BottomNav activeTab="insights" onNavigate={onNavigate} />
    </div>
  );
};

export default InsightsScreen;
